/**
 * `WikilinkSuggestion` — `[[` autocomplete for the rich editor (2026-06-23).
 *
 * Typing `[[` opens a small popup of note titles (fuzzy-filtered over the cached `note_titles`
 * set, the same source the source-view autocomplete + the wikilink resolver use). Selecting one
 * inserts a `WikilinkNode` chip (title + captured noteId). It's a self-contained ProseMirror
 * plugin — no `@tiptap/suggestion` dependency — that mirrors the source-view behaviour in
 * `wikilink-autocomplete.ext.ts`.
 *
 * When the partial matches no existing title the popup simply closes: the user can keep typing
 * and the WikilinkNode input rule converts the full `[[New Title]]` into a chip on the closing
 * `]]`, so brand-new (forward-reference) links still work.
 */

import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';
import type { TitleCompletionEntry } from './editorTypes';

export interface WikilinkSuggestionOptions {
  /** The live cached title set — read synchronously at keystroke time (never awaits). */
  getTitles: () => TitleCompletionEntry[];
}

const KEY = new PluginKey('wikilinkSuggestion');
/** `[[` followed by a partial title (no closing `]]`) immediately left of the cursor. */
const OPEN_BEFORE_CURSOR = /\[\[([^\]\n]*)$/;
/** Max completions shown. */
const MAX_ITEMS = 12;
/** How far back to scan for the opening `[[` (bounds the textBetween read). */
const LOOKBACK = 240;

/** Subsequence fuzzy score — every char of `needle` appears in `haystack` in order; prefix wins.
 *  Mirrors the source-view scorer so ranking is consistent. Returns null for no match. */
function fuzzyScore(needle: string, haystack: string): number | null {
  if (needle === '') return 0;
  const n = needle.toLowerCase();
  const h = haystack.toLowerCase();
  if (h.startsWith(n)) return 1000 - haystack.length;
  let hi = 0;
  let score = 0;
  let lastHit = -2;
  for (let ni = 0; ni < n.length; ni += 1) {
    const found = h.indexOf(n[ni], hi);
    if (found === -1) return null;
    score += found === lastHit + 1 ? 5 : 1;
    lastHit = found;
    hi = found + 1;
  }
  return score;
}

interface SuggestState {
  active: boolean;
  /** Doc position of the first `[` of the opening `[[`. */
  from: number;
  query: string;
  index: number;
  matches: TitleCompletionEntry[];
}

const INACTIVE: SuggestState = { active: false, from: 0, query: '', index: 0, matches: [] };

export const WikilinkSuggestion = Extension.create<WikilinkSuggestionOptions>({
  name: 'wikilinkSuggestion',

  addOptions() {
    return { getTitles: () => [] };
  },

  addProseMirrorPlugins() {
    const getTitles = this.options.getTitles;
    let state: SuggestState = INACTIVE;
    let dropdown: HTMLDivElement | null = null;

    const destroyDropdown = (): void => {
      if (dropdown !== null) {
        dropdown.remove();
        dropdown = null;
      }
    };

    const close = (): void => {
      state = INACTIVE;
      destroyDropdown();
    };

    const computeMatches = (query: string): TitleCompletionEntry[] => {
      const scored: { entry: TitleCompletionEntry; score: number }[] = [];
      for (const entry of getTitles()) {
        const s = fuzzyScore(query, entry.title);
        if (s !== null) scored.push({ entry, score: s });
      }
      scored.sort((a, b) => b.score - a.score);
      return scored.slice(0, MAX_ITEMS).map((x) => x.entry);
    };

    const insert = (view: EditorView, entry: TitleCompletionEntry): void => {
      const wikilink = view.state.schema.nodes.wikilink;
      if (wikilink === undefined) {
        close();
        return;
      }
      const to = view.state.selection.from;
      const node = wikilink.create({ title: entry.title, noteId: entry.noteId });
      const tr = view.state.tr.replaceRangeWith(state.from, to, node);
      // A trailing space so the cursor exits the atom and the user keeps typing naturally.
      view.dispatch(tr.insertText(' '));
      view.focus();
      close();
    };

    const renderDropdown = (view: EditorView): void => {
      if (!state.active || state.matches.length === 0) {
        destroyDropdown();
        return;
      }
      if (dropdown === null) {
        dropdown = document.createElement('div');
        dropdown.className = 'wikilink-suggest';
        document.body.appendChild(dropdown);
      }
      dropdown.innerHTML = '';
      state.matches.forEach((entry, i) => {
        const item = document.createElement('button');
        item.type = 'button';
        item.className =
          'wikilink-suggest__item' + (i === state.index ? ' wikilink-suggest__item--active' : '');
        item.textContent = entry.title;
        // mousedown (not click) so the editor doesn't lose focus before we insert.
        item.addEventListener('mousedown', (e) => {
          e.preventDefault();
          insert(view, entry);
        });
        dropdown!.appendChild(item);
      });
      // Position just under the cursor (fixed → viewport coords from coordsAtPos).
      const coords = view.coordsAtPos(view.state.selection.from);
      dropdown.style.left = `${Math.round(coords.left)}px`;
      dropdown.style.top = `${Math.round(coords.bottom + 4)}px`;
    };

    const recompute = (view: EditorView): void => {
      const sel = view.state.selection;
      if (!sel.empty) {
        if (state.active) close();
        return;
      }
      const $from = sel.$from;
      const lookFrom = Math.max(0, $from.parentOffset - LOOKBACK);
      const textBefore = $from.parent.textBetween(lookFrom, $from.parentOffset, undefined, '￼');
      const m = OPEN_BEFORE_CURSOR.exec(textBefore);
      if (m === null) {
        if (state.active) close();
        return;
      }
      const query = m[1];
      const matches = computeMatches(query);
      if (matches.length === 0) {
        // No existing-note match — let the input rule handle a brand-new `[[Title]]`.
        if (state.active) close();
        return;
      }
      const from = sel.from - query.length - 2; // position of the first `[`
      const index = state.active ? Math.min(state.index, matches.length - 1) : 0;
      state = { active: true, from, query, index, matches };
      renderDropdown(view);
    };

    return [
      new Plugin({
        key: KEY,
        view() {
          return {
            update: (view) => recompute(view),
            destroy: () => close(),
          };
        },
        props: {
          handleKeyDown(view, event) {
            if (!state.active) return false;
            if (event.key === 'ArrowDown') {
              state.index = (state.index + 1) % state.matches.length;
              renderDropdown(view);
              return true;
            }
            if (event.key === 'ArrowUp') {
              state.index = (state.index - 1 + state.matches.length) % state.matches.length;
              renderDropdown(view);
              return true;
            }
            if (event.key === 'Enter' || event.key === 'Tab') {
              insert(view, state.matches[state.index]);
              return true;
            }
            if (event.key === 'Escape') {
              close();
              return true;
            }
            return false;
          },
        },
      }),
    ];
  },
});
